/**
 * Reservation Service - Single Source of Truth
 *
 * Provides functions to interact with reservation Edge Functions:
 * - confirm-reservation: Create appointment directly (no temporary reservation needed)
 * - check-availability: Check slot availability for a date/time
 * - cancel-reservation: Cancel an appointment via the link in the confirmation email
 *
 * SECURITY NOTE:
 * - Edge Functions are public (no JWT required)
 * - Edge Functions use SERVICE_ROLE internally
 * - Slot conflict check prevents double-booking
 */

// Re-export Appointment type from config for convenience
export type { Appointment } from '../config/supabase';

// Environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// =========================================================================
// Types
// =========================================================================

export type ServiceType = 'store' | 'home';

/**
 * Appointment summary with device details, price, and condition info.
 * Used to enrich email and calendar event.
 */
export interface AppointmentSummary {
  /** Device model label, e.g. "iPhone 15 Pro" */
  device_label?: string;
  /** Storage capacity, e.g. "256 GB" */
  storage_label?: string;
  /** Estimated/quoted price in EUR */
  estimated_price_eur?: number;
  /** Service type label, e.g. "In de winkel" or "Ophaalservice" */
  service_type_label?: string;
  /** Location name, e.g. "Phone Bros Gent" */
  location_label?: string;
  /** Human-readable summary of condition answers */
  answers_summary?: string;
  /** UI language for email templates */
  language?: string;
  /** Additional notes from customer */
  notes?: string;
  /** Cart items with full details for email template */
  cart_items?: Array<{
    modelName: string;
    deviceType: string;
    image: string;
    price: number;       // 0 = on-demand
    quantity: number;
    storage?: string;
    answers?: Array<{ question: string; question_nl?: string; answer: string; answer_nl?: string }>;
  }>;
}

/**
 * Direct booking request parameters (no temporary reservation needed)
 */
export interface CreateAppointmentParams {
  date: string;              // YYYY-MM-DD
  time: string;              // HH:MM
  serviceType: ServiceType;
  location?: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  appointmentSummary?: AppointmentSummary;
}

/**
 * Response from confirmReservation
 */
export interface ConfirmReservationResult {
  success: boolean;
  appointment?: import('../config/supabase').Appointment;
  error?: string;
  errorCode?: string;
}

/**
 * Request parameters for fetching booked slots
 */
export interface BookedSlotsRequest {
  date: string; // YYYY-MM-DD
  location?: string;
  serviceType?: ServiceType;
}

/**
 * Response from availability check
 */
export interface AvailabilityResult {
  available: boolean;
  reason?: string;
}

// =========================================================================
// Session Management
// =========================================================================

const SESSION_STORAGE_KEY = 'pb_session_id';

/**
 * Get or create a session ID for the current user session.
 * Stores it in localStorage for persistence across page reloads.
 */
export function getOrCreateSessionId(): string {
  let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);

  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }

  return sessionId;
}

// =========================================================================
// Core API Functions
// =========================================================================

/**
 * Create a permanent appointment directly (Direct Booking).
 * No temporary reservation needed - backend checks slot availability.
 *
 * @param params - Appointment parameters including customer info
 * @returns Promise with success status and appointment data or error
 */
export async function confirmReservation(
  params: CreateAppointmentParams
): Promise<ConfirmReservationResult> {
  try {
    if (!supabaseUrl) {
      return { success: false, error: 'Supabase URL missing' };
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/confirm-reservation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        date: params.date,
        time: params.time,
        service_type: params.serviceType,
        location: params.location || null,
        customer_name: params.customerName,
        customer_email: params.customerEmail,
        customer_phone: params.customerPhone || null,
        appointment_summary: params.appointmentSummary || null,
      }),
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message = payload.error || payload.message || `HTTP ${response.status}`;
      const errorCode = payload.code || undefined;
      console.error('Appointment creation error:', message, payload);
      return { success: false, error: message, errorCode };
    }

    return {
      success: true,
      appointment: payload.appointment,
    };
  } catch (error: any) {
    console.error('Appointment creation error:', error);
    return { success: false, error: error.message || 'Unknown error' };
  }
}

/**
 * Check availability for a specific date and time slot.
 *
 * @param date - Date in YYYY-MM-DD format
 * @param time - Time in HH:MM format
 * @returns Promise with availability status
 */
export async function checkAvailability(
  date: string,
  time: string
): Promise<AvailabilityResult> {
  try {
    if (!supabaseUrl) {
      return { available: false, reason: 'Configuration missing' };
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/check-availability`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({ date, time }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Availability check error:', errorData);
      return { available: false, reason: 'Check failed' };
    }

    const data = await response.json();
    return {
      available: data.available,
      reason: data.reason,
    };
  } catch (error: any) {
    console.error('Availability check error:', error);
    return { available: false, reason: 'Check failed' };
  }
}

/**
 * Fetch all booked time slots for a given date.
 * Used to disable fully booked slots in the UI.
 *
 * @param params - Date and optional filters
 * @returns Promise with array of booked time slots (e.g., ["10:00", "12:00", "14:30"])
 */
export async function fetchBookedSlotsForDate(
  params: BookedSlotsRequest
): Promise<string[]> {
  if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('Supabase configuration missing. Returning empty booked slots.');
    return [];
  }

  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/check-availability`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        date: params.date,
        mode: 'day',
        location: params.location,
        service_type: params.serviceType,
      }),
    });

    if (!response.ok) {
      console.error('Failed to fetch booked slots:', response.status, response.statusText);
      return [];
    }

    const data = await response.json();

    // Response format: { date, bookedSlots: string[], totalBooked: number }
    if (data.bookedSlots && Array.isArray(data.bookedSlots)) {
      return data.bookedSlots;
    }

    return [];
  } catch (err) {
    console.error('Error fetching booked slots:', err);
    return [];
  }
}

/**
 * Cancel an appointment from the link in the confirmation email.
 * Only called after the customer clicks the confirm button on the cancel page,
 * never on page load, so mail link scanners cannot cancel a booking.
 *
 * The response body is read for non-2xx statuses too, so the specific code
 * (too_late, rate_limited, ...) is preserved.
 *
 * @param orderNumber - Order number from the email link, e.g. "PB-20240115-123456"
 * @param token - Cancellation token from the email link
 * @param lang - UI language of the customer
 * @returns Promise with success flag and result code
 */
export async function cancelReservation(
  orderNumber: string,
  token: string,
  lang?: string
): Promise<{ success: boolean; code: string }> {
  try {
    if (!supabaseUrl) {
      console.error('Appointment cancellation error: Supabase URL missing');
      return { success: false, code: 'server_error' };
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/cancel-reservation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        order_number: orderNumber,
        token,
        lang: lang || 'nl',
      }),
    });

    // Also covers a body that is valid JSON but null, which would otherwise throw below
    const payload = (await response.json().catch(() => null)) ?? {};
    const code = typeof payload.code === 'string' && payload.code ? payload.code : 'server_error';

    if (!response.ok) {
      console.error('Appointment cancellation error:', response.status, payload);
    }

    return {
      success: response.ok && payload.success === true,
      code,
    };
  } catch (error: any) {
    console.error('Appointment cancellation error:', error);
    return { success: false, code: 'network_error' };
  }
}

// =========================================================================
// Deprecated Functions (kept for backward compatibility, will be removed)
// =========================================================================

/**
 * @deprecated Use confirmReservation instead. Direct booking flow is now standard.
 * This function is kept only for backward compatibility during migration.
 */
export async function createTemporaryReservation(
  date: string,
  time: string,
  serviceType: ServiceType,
  location?: string,
  sessionId?: string
): Promise<{ success: boolean; reservationId?: string; sessionId?: string; error?: string }> {
  console.warn('createTemporaryReservation is deprecated. Use confirmReservation instead.');

  const sessionIdToUse = sessionId || getOrCreateSessionId();

  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/create-temporary-reservation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        date,
        time,
        service_type: serviceType,
        location: location || null,
        session_id: sessionIdToUse,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Temporary reservation creation error:', errorData);
      return {
        success: false,
        error: errorData.error || errorData.message || 'Failed to create temporary reservation'
      };
    }

    const data = await response.json();
    const reservationId = data.temporary_reservation?.id || data.reservation?.id || data.reservationId;

    if (!reservationId) {
      console.error('Reservation ID not found in response:', data);
      return { success: false, error: 'Reservation ID not received' };
    }

    return {
      success: true,
      reservationId,
      sessionId: sessionIdToUse,
    };
  } catch (error: any) {
    console.error('Temporary reservation error:', error);
    return { success: false, error: error.message || 'Unknown error' };
  }
}

/**
 * @deprecated Temporary reservations are no longer used in direct booking flow.
 * This function is kept only for backward compatibility during migration.
 */
export async function cleanupTemporaryReservation(
  reservationId?: string,
  sessionId?: string
): Promise<{ success: boolean; error?: string }> {
  console.warn('cleanupTemporaryReservation is deprecated. Temporary reservations are no longer used.');

  if (!reservationId && !sessionId) {
    return { success: false, error: 'Reservation ID or Session ID required' };
  }

  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/cleanup-temporary-reservation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        id: reservationId,
        session_id: sessionId,
      }),
    });

    // 404 = already deleted, considered success
    if (response.status === 404) {
      return { success: true };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Temporary reservation cleanup error:', errorData);
      return { success: false, error: errorData.error || 'Failed to cleanup' };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Temporary reservation cleanup error:', error);
    return { success: false, error: error.message || 'Unknown error' };
  }
}
